import { generateCryptoSafeString, Logger } from '@dvb/common';
import { faSignIn } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, cn } from '@heroui/react';
import { getSystemConfigOptions } from '@milestone-portal/api/actions/system/system.options';
import { SSO_DATA } from '@milestone-portal/common/constants';
import { useSettingStore } from '@milestone-portal/context/useSettingStore';
import { toast } from '@milestone-portal/utils/toast';
import { useQuery } from '@tanstack/react-query';
import pkceChallenge from 'pkce-challenge';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';

interface Props {
  className?: string;
  variant?: 'primary' | 'secondary';
}

export function SSOLoginButton({
  className = undefined,
  variant = 'primary',
}: Props) {
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const [isPending, setIsPending] = useState(false);
  const [, setSsoData] = useSessionStorage<{
    codeVerifier: string;
    state: string;
  } | null>(SSO_DATA, null);
  const { data } = useQuery(getSystemConfigOptions());
  const { isDarkMode } = useSettingStore();

  const handleLogin = useCallback(async () => {
    setIsPending(true);

    try {
      const clientId = data?.sso?.client_id;
      const authEndpoint = data?.sso?.endpoint;

      if (!clientId || !authEndpoint) {
        toast.error(t('unknownError', { ns: 'ui' }));
        setIsPending(false);
        return;
      }

      const state = generateCryptoSafeString();

      const { code_challenge: codeChallenge, code_verifier: codeVerifier } =
        await pkceChallenge();

      setSsoData({
        codeVerifier,
        state,
      });

      const url = new URL(authEndpoint);
      url.searchParams.set('client_id', clientId);
      url.searchParams.set('state', state);
      url.searchParams.set('code_challenge', codeChallenge);
      url.searchParams.set('code_challenge_method', 'S256');
      url.searchParams.set('redirect_uri', `${window.location.origin}/login`);
      url.searchParams.set('response_type', 'code');
      url.searchParams.set('scope', 'view-user');
      url.searchParams.set('theme', isDarkMode ? 'dark' : 'light');

      if (language) {
        url.searchParams.set('locale', language.replace('-', '_'));
      }

      setIsPending(false);
      window.location.href = url.toString();
    } catch (error: any) {
      setIsPending(false);
      Logger.error(error.message);
    }
  }, [data, isDarkMode, language, setIsPending, setSsoData, t]);

  if (variant === 'primary') {
    return (
      <Button
        className={cn('bg-primary-500/90 font-bold text-black/90', className)}
        isLoading={isPending}
        onPress={handleLogin}
        radius="full"
        startContent={isPending ? null : <FontAwesomeIcon icon={faSignIn} />}
      >
        {t('login')}
      </Button>
    );
  }

  return (
    <Button
      className={className}
      onPress={handleLogin}
      size="sm"
      startContent={<FontAwesomeIcon icon={faSignIn} />}
      variant="flat"
    >
      {t('login')}
    </Button>
  );
}
